/**
 * AI Chat Widget Component
 * Reusable floating chat widget for property pages
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { tokenStorage } from "../../config/api";
import { useChatMutation } from "../../store/api/aiChatApi";
import { useGetProfileQuery } from "../../store/api/authApi";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Input from "../ui/Input";

interface Message {
	id: number;
	text: string;
	sender: "user" | "bot";
	timestamp: Date;
}

interface AIChatWidgetProps {
	propertyId?: string;
	propertyName?: string;
	onClose?: () => void;
}

const AIChatWidget: React.FC<AIChatWidgetProps> = ({
	propertyId,
	propertyName,
	onClose,
}) => {
	const navigate = useNavigate();
	// Only fetch profile if user has a token (skip query if no token to avoid 403 errors)
	const accessToken = tokenStorage.getAccessToken();
	const { data: currentUser } = useGetProfileQuery(
		accessToken ? undefined : skipToken
	);
	const [chat, { isLoading: isChatLoading }] = useChatMutation();
	const [messages, setMessages] = useState<Message[]>([
		{
			id: 1,
			text: propertyName
				? `Hello! I'm here to help you with inquiries about ${propertyName}. What would you like to know?`
				: "Hello! I'm here to help you with any property inquiries. How can I assist you today?",
			sender: "bot",
			timestamp: new Date(),
		},
	]);
	const [inputMessage, setInputMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isMinimized, setIsMinimized] = useState(false);
	const [conversationId, setConversationId] = useState<string | null>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const scrollToBottom = () => {
		messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
		e?.preventDefault();
		if (!inputMessage?.trim() || isLoading || isChatLoading) return;

		const userMessage: Message = {
			id: Date.now(),
			text: inputMessage,
			sender: "user",
			timestamp: new Date(),
		};

		setMessages((prev) => [...prev, userMessage]);
		const messageToSend = inputMessage;
		setInputMessage("");
		setIsLoading(true);

		try {
			// Use backend API with conversation_id for context
			const response = await chat({
				message: messageToSend,
				property_id: propertyId || undefined,
				conversation_id: conversationId || undefined,
			}).unwrap();

			// Update conversation ID if this is a new conversation
			if (response.conversation_id && !conversationId) {
				setConversationId(response.conversation_id);
			}

			const botMessage: Message = {
				id: Date.now() + 1,
				text:
					response.message ||
					"I apologize, but I couldn't generate a response. Please try again.",
				sender: "bot",
				timestamp: new Date(),
			};

			setMessages((prev) => [...prev, botMessage]);
		} catch (error: any) {
			console.error("Chat error:", error);
			const errorMessage: Message = {
				id: Date.now() + 1,
				text:
					error?.data?.error ||
					error?.data?.message ||
					"I apologize, but I'm having trouble connecting right now. Please try again in a moment, or feel free to contact us directly for immediate assistance.",
				sender: "bot",
				timestamp: new Date(),
			};
			setMessages((prev) => [...prev, errorMessage]);
		} finally {
			setIsLoading(false);
		}
	};

	const formatTime = (timestamp: Date) => {
		return timestamp?.toLocaleTimeString("en-US", {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const quickResponses = [
		"What's the price?",
		"Can I schedule a viewing?",
		"Tell me about the neighborhood",
		"What are the property features?",
		"Is financing available?",
	];

	const handleQuickResponse = (text: string) => {
		setInputMessage(text);
		inputRef?.current?.focus();
	};

	const handleOpenFullChat = () => {
		// Navigate to full AI assistant page with property context
		if (currentUser) {
			const params = new URLSearchParams();
			if (propertyId) params.set("property_id", propertyId);
			if (conversationId) params.set("conversation_id", conversationId);
			navigate(`/ai-assistant?${params.toString()}`);
		} else {
			// If not logged in, redirect to login with return URL
			navigate(
				`/login?redirect=/ai-assistant${
					propertyId ? `?property_id=${propertyId}` : ""
				}`,
			);
		}
	};

	if (isMinimized) {
		return (
			<div className="fixed right-4 bottom-4 z-50 bg-card border border-border rounded-lg shadow-2xl">
				<button
					onClick={() => setIsMinimized(false)}
					className="w-full p-4 flex items-center justify-between hover:bg-muted transition-colors"
				>
					<div className="flex items-center space-x-3">
						<div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
							<Icon name="MessageSquare" size={20} className="text-primary" />
						</div>
						<div className="text-left">
							<p className="text-sm font-medium text-foreground">
								AI Assistant
							</p>
							<p className="text-xs text-muted-foreground">
								{messages.length} messages
							</p>
						</div>
					</div>
					<Icon name="Maximize2" size={16} className="text-muted-foreground" />
				</button>
			</div>
		);
	}

	return (
		<div className="fixed right-4 bottom-4 z-50 bg-card border border-border rounded-lg shadow-2xl w-96 h-[600px] flex flex-col">
			{/* Header */}
			<div className="flex items-center justify-between p-4 border-b border-border bg-primary text-primary-foreground rounded-t-lg">
				<div className="flex items-center space-x-3">
					<div className="w-10 h-10 bg-primary-foreground/20 rounded-full flex items-center justify-center">
						<Icon name="Bot" size={20} />
					</div>
					<div>
						<p className="font-semibold">AI Property Assistant</p>
						{propertyName && (
							<p className="text-xs text-primary-foreground/80">
								{propertyName}
							</p>
						)}
					</div>
				</div>
				<div className="flex items-center space-x-2">
					<button
						onClick={handleOpenFullChat}
						className="p-1.5 hover:bg-primary-foreground/20 rounded transition-colors"
						title="Open in full screen"
					>
						<Icon name="Maximize2" size={16} />
					</button>
					<button
						onClick={() => setIsMinimized(true)}
						className="p-1.5 hover:bg-primary-foreground/20 rounded transition-colors"
						title="Minimize"
					>
						<Icon name="ChevronDown" size={16} />
					</button>
					{onClose && (
						<button
							onClick={onClose}
							className="p-1.5 hover:bg-primary-foreground/20 rounded transition-colors"
							title="Close"
						>
							<Icon name="X" size={16} />
						</button>
					)}
				</div>
			</div>

			{/* Messages */}
			<div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
				{messages.map((msg) => (
					<div
						key={msg.id}
						className={`flex ${
							msg.sender === "user" ? "justify-end" : "justify-start"
						}`}
					>
						<div
							className={`max-w-[80%] rounded-lg p-3 ${
								msg.sender === "user"
									? "bg-primary text-primary-foreground"
									: "bg-card border border-border text-foreground"
							}`}
						>
							<p className="text-sm whitespace-pre-wrap break-words">
								{msg.text}
							</p>
							<p
								className={`text-xs mt-1 ${
									msg.sender === "user"
										? "text-primary-foreground/70"
										: "text-muted-foreground"
								}`}
							>
								{formatTime(msg.timestamp)}
							</p>
						</div>
					</div>
				))}
				{isLoading && (
					<div className="flex justify-start">
						<div className="bg-card border border-border rounded-lg p-3">
							<div className="flex items-center space-x-2">
								<Icon
									name="Loader2"
									size={16}
									className="animate-spin text-primary"
								/>
								<span className="text-sm text-muted-foreground">
									AI is thinking...
								</span>
							</div>
						</div>
					</div>
				)}
				<div ref={messagesEndRef} />
			</div>

			{/* Quick Responses */}
			{messages.length === 1 && (
				<div className="px-4 py-2 border-t border-border bg-card">
					<div className="flex flex-wrap gap-2">
						{quickResponses.slice(0, 3).map((text, index) => (
							<button
								key={index}
								onClick={() => handleQuickResponse(text)}
								className="text-xs px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-full text-foreground transition-colors"
							>
								{text}
							</button>
						))}
					</div>
				</div>
			)}

			{/* Input */}
			<form
				onSubmit={handleSendMessage}
				className="p-4 border-t border-border bg-card"
			>
				<div className="flex items-end gap-2">
					<div className="flex-1">
						<Input
							ref={inputRef}
							value={inputMessage}
							onChange={(e) => setInputMessage(e.target.value)}
							placeholder="Ask about this property..."
							disabled={isLoading || isChatLoading}
							className="w-full"
						/>
					</div>
					<Button
						type="submit"
						disabled={!inputMessage.trim() || isLoading || isChatLoading}
						size="sm"
						iconName="Send"
					>
						Send
					</Button>
				</div>
				{!currentUser && (
					<p className="text-xs text-muted-foreground mt-2 text-center">
						<a
							href="/login"
							className="text-primary hover:underline"
							onClick={(e) => {
								e.preventDefault();
								navigate("/login?redirect=" + window.location.pathname);
							}}
						>
							Sign in
						</a>{" "}
						for full chat history
					</p>
				)}
			</form>
		</div>
	);
};

export default AIChatWidget;
